/* eslint-disable @typescript-eslint/ban-ts-comment */
import { CSSProperties, FC, useEffect, useState } from 'react'

import { showToast } from '../../../../common/utils'

import {
  DocumentList,
  HelpLink,
  Spinner,
  AccessRestriction,
} from '../../../../components/generic'
import { sizes } from '@lmc/common/constants'
import ConfirmationDialog from '../../../../LEGACY/ConfirmationDialog'
import Selectors from '../../../../selectors'
import Glyph from '../../../../LEGACY/Glyph'
import { Button, EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { useSelector, useDispatch } from 'react-redux'
import {
  getNoDocumentsMessage,
  getSupportSummary,
  getSupportSummarySeniors,
  groupDocuments,
  MODAL_ID,
  SUPPORT_ARTICLE,
} from './utils'
import actions from '../../../../actions'
import { Resident, Document } from '@lmc/ts-common'
import { useParams } from 'react-router-dom'
import Operations from '../../../../operations'
import { isBrowser, isTablet } from 'react-device-detect'
import { history } from '../../../../store'
import useResident from 'hooks/useResident'

const isDesktop = isBrowser || isTablet

const ResidentDocumentsScreen: FC = () => {
  const dispatch = useDispatch()

  const params = useParams()

  const id = params?.id

  const { resident, isLoading: isResidentLoading } = useResident({ id })

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative: string } } }) =>
      state.global.home.preferredPeopleNominative
  )

  const user = useSelector(
    (state: { global: { user: Resident } }) => state.global.user
  )

  const documents = useSelector((state) =>
    // @ts-ignore
    Selectors.carePlanDocumentSelector(state, { router: { params } })
  )

  const [isLoading, setIsLoading] = useState(false)

  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)

  const fetchData = async () => {
    if (!documents || !documents.length) {
      setIsLoading(false)
    }

    if (id) {
      await dispatch(Operations.loadList('documents', { resident: id }, false))
    }

    await dispatch(
      Operations.loadList(
        'care-plans',
        {
          resident: id,
          lite: 1,
          isRevision: false,
        },
        false
      )
    )

    await dispatch(
      Operations.loadList(
        'care-plans',
        {
          resident: id,
          lite: 1,
          isRevision: false,
        },
        false
      )
    )

    setIsLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [params])

  const openDialog = (id: string) => setDocumentToDelete(id)

  const closeDialog = () => setDocumentToDelete(null)

  const handleDelete = () => {
    dispatch(Operations.deleteDocument(documentToDelete, 'documents'))
    showToast({ type: 'info', title: 'Deleted a Document' })
    closeDialog()
  }

  const openCreateModal = () => {
    dispatch(
      actions.openModal({
        modalId: MODAL_ID,
        data: { listId: 'documents', type: 'create' },
      })
    )
    dispatch(
      actions.addForm({
        formId: MODAL_ID,
        fields: { resident: id } as any,
      })
    )
  }

  const openEditModal = (document: Document) => {
    dispatch(
      actions.openModal({
        modalId: MODAL_ID,
        data: { listId: 'documents', type: 'edit' },
      })
    )
    dispatch(
      actions.addForm({
        formId: MODAL_ID,
        initial: document,
      })
    )
  }

  const renderBody = ({
    groupedDocs,
    hasDocuments,
  }: {
    groupedDocs: unknown[]
    hasDocuments?: boolean
  }) => {
    if (isLoading) return <Spinner style={styles.spinner} />

    if (hasDocuments) {
      return (
        <DocumentList
          documents={groupedDocs}
          listId="documents"
          onDelete={openDialog}
          onEdit={openEditModal}
          carePlanNominative={carePlanNominative}
        />
      )
    }

    const emptyStateDescription =
      (user as unknown as { role: string }).role === 'senior-carer'
        ? getSupportSummarySeniors(carePlanNominative)
        : getSupportSummary(carePlanNominative)

    return (
      // @ts-ignore
      <EmptyState
        title={getNoDocumentsMessage(preferredPeopleNominative)}
        icon={<NoDataIcon />}
        description={emptyStateDescription}
      />
    )
  }

  if (!documents || !params || !params.id || !user) {
    return <Spinner style={styles.spinner} />
  }

  const { hasDocuments, groupedDocs } = groupDocuments(
    documents,
    carePlanNominative
  )

  const pageHeaderTitle =
    isResidentLoading || !resident
      ? 'Documents'
      : `${resident?.name?.first} ${resident?.name?.last}'s Documents`

  return (
    <>
      <PageHeader
        title={pageHeaderTitle}
        onBack={!isDesktop ? () => history.back?.() : undefined}
        sticky
      >
        <div>
          <AccessRestriction>
            <Button
              onClick={openCreateModal}
              variant="primary"
              icon={
                <Glyph
                  name="plus"
                  aphroditeStyles={undefined}
                  className={undefined}
                  color={undefined}
                  component={undefined}
                  size={undefined}
                  style={undefined}
                />
              }
            >
              Add a Document
            </Button>
          </AccessRestriction>
        </div>
      </PageHeader>
      <div style={styles.container as CSSProperties}>
        <div style={styles.documentListContainer}>
          {renderBody({ groupedDocs, hasDocuments } as unknown as {
            groupedDocs: unknown[]
            hasDocuments?: boolean | undefined
          })}
        </div>
        <AccessRestriction>
          {/*
       // @ts-ignore */}
          <HelpLink supportLink={SUPPORT_ARTICLE} />
        </AccessRestriction>
        <ConfirmationDialog
          confirmationType="warning"
          isOpen={!!documentToDelete}
          onCancel={closeDialog}
          onConfirmation={handleDelete}
        >
          <div>
            Are you sure you want to delete this document? If you go ahead, it
            can&apos;t be undone. Once it&apos;s gone, it&apos;s gone for good!
          </div>
        </ConfirmationDialog>
      </div>
    </>
  )
}

const styles = {
  container: {
    padding: '24px 0px 50px',
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    display: 'flex',
    flexDirection: 'column',
  },
  documentListContainer: {
    paddingTop: 24,
  },
  noDocumentsMessage: {
    marginBottom: 20,
  },
  spinner: { minHeight: 194 },
}

export default ResidentDocumentsScreen
